import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { GearCardView, TIER_LABEL, MODE_LABEL } from "@/lib/gear";
import { PieceEditor } from "@/components/builds/PieceEditor";

const clone = (x) => JSON.parse(JSON.stringify(x));
const rx = (t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const swapSetInWhy = (why, from, to, kind, catalog) => {
  const ob = catalog.sets[from]?.bonus || "", nb = catalog.sets[to].bonus;
  const armRe = /(?<=^|\s)[A-Z][\w'-]*(?: [A-Z][\w'-]*)? on weapon and armor: .*?\.(?=\s|$)/;
  if (kind === "armament") {
    const s = `${to} on weapon and armor: ${nb}.`;
    return armRe.test(why) ? why.replace(armRe, s) : `${why} ${s}`.trim();
  }
  const m = why.match(armRe), cut = m ? why.indexOf(m[0]) : why.length;
  let head = why.slice(0, cut);
  if (ob && head.startsWith(`${ob} fits`)) head = `${nb} fits${head.slice(ob.length + 5)}`;
  head = head.replace(new RegExp(`\\b${rx(from)}\\b`, "g"), to);
  return head + why.slice(cut);
};

export const BuildEditor = ({ b, catalog, assets, me, onSaved, onDirty }) => {
  const [mode, setMode] = useState("pvp");
  const [tier, setTier] = useState("amazing");
  const [modes, setModes] = useState(() => clone(b.modes));
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setModes(clone(b.modes)); setDirty(false); }, [b.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { onDirty(dirty); }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps

  const cur = modes[mode][tier];
  const edit = (fn) => { const m = clone(modes); fn(m[mode][tier]); setModes(m); setDirty(true); };
  const save = async (extra = {}) => {
    setBusy(true);
    try {
      const data = { ...b, modes, editedBy: me.email, editedAt: new Date().toISOString(), ...extra };
      delete data.id;
      await api.put("/db/doc", { path: `gearBuilds/${b.id}`, data });
      toast.success(`Saved ${b.name}`); setDirty(false); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const publish = async (un) => {
    if (dirty) await save();
    try { await api.post("/builds/publish", { ids: [b.id], unpublish: un }); toast.success(un ? "Back to draft" : `Published ${b.name}`); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const resetTier = () => edit((t) => { Object.assign(t, clone(b.auto[mode][tier])); });

  return (
    <div data-testid="build-editor">
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 20, marginRight: "auto" }}>{b.name} <span style={{ color: "var(--muted)", fontSize: 13 }}>{"★".repeat(b.stars || 0)} · {b.class} · {b.troop}</span></h3>
        <span className="lg-chip" data-testid="build-status">{b.status === "published" ? "Published" : "Draft"}</span>
        <button className="lg-btn sm" disabled={busy || !dirty} onClick={() => save()} data-testid="build-save">{busy ? "Saving…" : dirty ? "Save changes" : "Saved"}</button>
        {b.status === "published"
          ? <button className="lg-btn ghost sm" onClick={() => publish(true)} data-testid="build-unpublish">Unpublish</button>
          : <button className="lg-btn sm" onClick={() => publish(false)} data-testid="build-publish">Publish</button>}
      </div>
      <div className="lg-seg" style={{ marginBottom: 8 }}>
        {Object.entries(MODE_LABEL).map(([k, l]) => <button key={k} className={mode === k ? "on" : ""} onClick={() => setMode(k)} data-testid={`build-mode-${k}`}>{l}</button>)}
      </div>
      <div className="lg-seg" style={{ marginBottom: 12 }}>
        {Object.entries(TIER_LABEL).map(([k, l]) => <button key={k} className={tier === k ? "on" : ""} onClick={() => setTier(k)} data-testid={`build-tier-${k}`}>{l}</button>)}
        <button className="ghost" onClick={resetTier} title="Put back the auto build for this tier" data-testid="build-reset-tier">Reset to auto</button>
      </div>
      <label style={{ display: "block", fontSize: 12, color: "var(--muted)" }}>Why this build (changing a set keeps all its pieces matched and updates the set name here)</label>
      <textarea className="lg-input" rows={2} value={cur.why || ""} onChange={(e) => edit((t) => { t.why = e.target.value; })} style={{ width: "100%", marginBottom: 12 }} data-testid="build-why" />
      <div className="lg-pieces">
        {cur.pieces.map((p, i) => (
          <div key={p.slot} className="lg-piece">
            <GearCardView piece={p} catalog={catalog} assets={assets} badge={TIER_LABEL[tier].toUpperCase()} />
            <PieceEditor p={p} catalog={catalog} onChange={(np) => edit((t) => {
              const old = t.pieces[i];
              t.pieces[i] = np;
              if (np.set !== old.set) {
                const kind = catalog.sets[np.set].kind;
                t.pieces.forEach((x) => { if (catalog.sets[x.set]?.kind === kind) x.set = np.set; });
                t.why = swapSetInWhy(t.why || "", old.set, np.set, kind, catalog);
              }
            })} />
          </div>
        ))}
      </div>
    </div>
  );
};
